import { useEffect, useState } from "react";
import { getRealReviews } from "@/api/content";
import { getBrandConfig } from "@/config/brand";
import type { PostCard } from "@/types/content";
import ArticleCard from "@/components/base/ArticleCard";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import { extractLocation } from "@/utils/extractLocation";

function extractStoreCount(title: string): string | null {
  const match = title.match(/TOP\s*(\d+)|(\d+)\s*間/);
  if (match) {
    const num = match[1] || match[2];
    return `${num} 間店家`;
  }
  return null;
}

export default function RealReviews() {
  const [reviews, setReviews] = useState<PostCard[]>([]);
  const [loading, setLoading] = useState(true);
  const config = getBrandConfig().realReviews;
  const global = getBrandConfig().global;

  useEffect(() => {
    getRealReviews(6).then((data) => {
      setReviews(data);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <h2 className="section-title">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-shield-star-line text-primary text-xl md:text-2xl" />
              </div>
              {config.title}
            </h2>
          </div>
          <SectionSkeleton type="cards" count={6} columns={3} surface="alt" aspectRatio="16/10" footerLines={2} />
        </div>
      </section>
    );
  }

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <h2 className="section-title">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-shield-star-line text-primary text-xl md:text-2xl" />
            </div>
            {config.title}
          </h2>
          <a
            href="/articles"
            className="text-sm text-slate-500 hover:text-primary transition-colors flex items-center gap-1 whitespace-nowrap"
          >
            {global.viewAll}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {reviews.map((review) => {
            const storeCount = extractStoreCount(review.title);
            return (
              <ArticleCard
                key={review.id}
                post={review}
                imageOverlay={
                  <div className="absolute top-3 right-3 bg-primary/90 backdrop-blur-sm px-2.5 py-1 rounded-lg">
                    <span className="text-white text-xs font-medium">合集評比</span>
                  </div>
                }
                footer={
                  <div className="space-y-1">
                    {storeCount && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-store-2-line" />
                        </div>
                        {storeCount}
                      </span>
                    )}
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-map-pin-line" />
                        </div>
                        {extractLocation(review.tags)}
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-book-open-line" />
                        </div>
                        {review.readingTime} {global.readTime}
                      </span>
                    </div>
                  </div>
                }
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}